import { CheckCircle, Star, Sparkles, TrendingUp, CreditCard, Users, Settings, Calendar, Video, FileText, Brain, UserCheck } from 'lucide-react';

export interface PricingFeature {
  name: string;
  description?: string;
  essential: boolean | string;
  advanced: boolean | string;
  enterprise: boolean | string;
  category?: string;
}

export interface PricingPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  period: string;
  features: string[];
  popular?: boolean;
  trialDays: number;
  buttonText: string;
  buttonVariant: 'default' | 'outline';
}

// Core pricing plans data
export const pricingPlans: PricingPlan[] = [
  {
    id: 'essential',
    name: 'Essential',
    description: 'Perfect for small youth programs getting started',
    price: 49,
    period: 'month',
    trialDays: 14,
    features: [
      'Unlimited students, staff & locations',
      'HiveShare: Built-in secure video platform - no more YouTube links or privacy risks for kids (100 stored minutes per month)',
      'Ask AI Expert',
      'Parent portal',
      'Class booking & management',
      'Payment services',
      'Bill Assist: Smart billing intelligence and insights',
      'HivePad: Task management and productivity notes',
      'Online waiver & policy management',
      'Electronic signature & audit tracking',
      'Waitlists with auto notification',
      'eCommerce',
      'In-app 2-way messaging'
    ],
    buttonText: 'Start Free Trial',
    buttonVariant: 'outline'
  },
  {
    id: 'advanced',
    name: 'Advanced',
    description: 'Best for growing programs with advanced needs',
    price: 99,
    period: 'month',
    trialDays: 14,
    popular: true,
    features: [
      'Everything in Essential, plus:',
      'HiveShare (1000 stored minutes per month)',
      'Additional 1000-minute storage packs available',
      'Expanded electronic document volume',
      'Form Designer',
      'Hive360 (integrates enrollment right on your website)',
      'Expanded Ask AI Expert usage'
    ],
    buttonText: 'Get Started',
    buttonVariant: 'default'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large programs requiring maximum features',
    price: 199,
    period: 'month',
    trialDays: 14,
    features: [
      'Everything in Advanced, plus:',
      'HiveShare (unlimited stored minutes per month)',
      'Unlimited electronic document volume',
      'Unlimited Ask AI Expert usage',
      'White-glove, personalized onboarding',
      'Concierge customer support'
    ],
    buttonText: 'Talk to Sales',
    buttonVariant: 'outline'
  }
];

// Detailed feature comparison data
export const pricingFeatures: PricingFeature[] = [
  {
    name: 'Unlimited students, staff & locations',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'core'
  },
  {
    name: 'HiveShare',
    description: 'Built-in secure video platform',
    essential: '100 stored minutes/month',
    advanced: '1000 stored minutes/month',
    enterprise: 'Unlimited',
    category: 'media'
  },
  {
    name: 'Ask AI Expert',
    description: 'AI-powered assistant for your business',
    essential: '50 queries/month',
    advanced: '250 queries/month',
    enterprise: 'Unlimited queries',
    category: 'ai'
  },
  {
    name: 'Parent portal',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'core'
  },
  {
    name: 'Class booking & management',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'core'
  },
  {
    name: 'Payment services',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'billing'
  },
  {
    name: 'Bill Assist',
    description: 'Smart billing intelligence and insights',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'billing'
  },
  {
    name: 'HivePad',
    description: 'Task management and productivity notes',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'productivity'
  },
  {
    name: 'Online waiver & policy management',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'documents'
  },
  {
    name: 'Electronic signature & audit tracking',
    description: 'Secure document signing with full audit trail',
    essential: 'Up to 5 eSignatures/month',
    advanced: 'Up to 50 eSignatures/month',
    enterprise: 'Unlimited',
    category: 'documents'
  },
  {
    name: 'Waitlists with auto notification',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'core'
  },
  {
    name: 'Website integration',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'integration'
  },
  {
    name: 'eCommerce',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'core'
  },
  {
    name: 'In-app 2-way messaging',
    essential: true,
    advanced: true,
    enterprise: true,
    category: 'communication'
  },
  {
    name: 'Form Designer',
    essential: false,
    advanced: true,
    enterprise: true,
    category: 'forms'
  },
  {
    name: 'Hive360',
    description: 'Integrates enrollment right on your website',
    essential: false,
    advanced: true,
    enterprise: true,
    category: 'integration'
  },
  {
    name: 'White-glove, personalized onboarding',
    essential: false,
    advanced: false,
    enterprise: true,
    category: 'support'
  },
  {
    name: 'Concierge customer support',
    essential: false,
    advanced: false,
    enterprise: true,
    category: 'support'
  }
];

// Utility functions
export const getPlanById = (id: string): PricingPlan | undefined => {
  return pricingPlans.find(plan => plan.id === id);
};

export const getPlanByName = (name: string): PricingPlan | undefined => {
  return pricingPlans.find(plan => plan.name.toLowerCase() === name.toLowerCase());
};

export const getPlanByPrice = (price: number): PricingPlan | undefined => {
  return pricingPlans.find(plan => plan.price === price);
};

export const getFeaturesForPlan = (planId: string): PricingFeature[] => {
  return pricingFeatures.filter(feature => {
    switch (planId) {
      case 'essential':
        return feature.essential !== false;
      case 'advanced':
        return feature.advanced !== false;
      case 'enterprise':
        return feature.enterprise !== false;
      default:
        return false;
    }
  });
};

export const getFeatureValue = (feature: PricingFeature, planId: string): boolean | string => {
  switch (planId) {
    case 'essential':
      return feature.essential;
    case 'advanced':
      return feature.advanced;
    case 'enterprise':
      return feature.enterprise;
    default:
      return false;
  }
};

export const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'core':
      return Star;
    case 'media':
      return Video;
    case 'ai':
      return Brain;
    case 'billing':
      return CreditCard;
    case 'productivity':
      return CheckCircle;
    case 'documents':
      return FileText;
    case 'forms':
      return Settings;
    case 'integration':
      return Settings;
    case 'communication':
      return Users;
    case 'support':
      return UserCheck;
    default:
      return Settings;
  }
};

export const getCategoryColor = (category: string) => {
  switch (category) {
    case 'core':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'media':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'ai':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'billing':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'productivity':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'documents':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'forms':
      return 'bg-teal-100 text-teal-800 border-teal-200';
    case 'integration':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'communication':
      return 'bg-cyan-100 text-cyan-800 border-cyan-200';
    case 'support':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    default:
      return 'bg-slate-100 text-slate-800 border-slate-200';
  }
};

// Helper function to match database plans with shared data
export const matchDatabasePlanWithSharedData = (dbPlan: any): PricingPlan | null => {
  // Try matching by price first (most reliable)
  const priceInDollars = Math.round(dbPlan.amount_cents / 100);
  let matchedPlan = getPlanByPrice(priceInDollars);
  
  if (matchedPlan) return matchedPlan;
  
  // Try matching by name
  matchedPlan = getPlanByName(dbPlan.name);
  if (matchedPlan) return matchedPlan;
  
  // Try matching by slug/id
  matchedPlan = getPlanById(dbPlan.slug || dbPlan.id);
  if (matchedPlan) return matchedPlan;
  
  return null;
};

// Get enhanced features for a database plan
export const getEnhancedFeaturesForDatabasePlan = (dbPlan: any): string[] => {
  const matchedPlan = matchDatabasePlanWithSharedData(dbPlan);
  
  if (matchedPlan) {
    return matchedPlan.features;
  }
  
  // Fallback to database features if available
  if (dbPlan.features && dbPlan.features.length > 0) {
    return dbPlan.features;
  }
  
  // Ultimate fallback
  return [
    '14-day free trial',
    'Cancel anytime'
  ];
};